import { FactoryProvider, Inject, Injectable, InjectionToken, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/* removed local storage. using session storage instead as none of the data needs to persist*/

export const SESSION_STORAGE = new InjectionToken('sessionStorageToken');

@Injectable({
    providedIn: 'root'
})
export class SessionStorage {
    private data: { [key: string]: string } = {};

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object
    ) { }

    public setItem(key: string, val: string): void {
        try {
            const storage = this.getSessionStorage();
            if (storage) {
                storage.setItem(key, val);
                return;
            }
        } catch {
            // Fall back to memory storage if sessionStorage is blocked.
        }

        this.data[key] = val;
    }

    public getItem(key: string): string {
        try {
            const storage = this.getSessionStorage();
            if (storage) {
                return storage.getItem(key);
            }
        } catch {
            // Fall back to memory storage if sessionStorage is blocked.
        }

        return this.data[key] ?? null;
    }

    public removeItem(key: string): void {
        try {
            const storage = this.getSessionStorage();
            if (storage) {
                storage.removeItem(key);
                return;
            }
        } catch {
            // Fall back to memory storage if sessionStorage is blocked.
        }

        delete this.data[key];
    }

    public clear(): void {
        try {
            const storage = this.getSessionStorage();
            if (storage) {
                storage.clear();
                return;
            }
        } catch {
            // Fall back to memory storage if sessionStorage is blocked.
        }

        this.data = {};
    }

    public get length(): number {
        try {
            const storage = this.getSessionStorage();
            if (storage) {
                return storage.length;
            }
        } catch {
            // Fall back to memory storage if sessionStorage is blocked.
        }

        return Object.keys(this.data).length;
    }

    public key(index: number): string|null {
        try {
            const storage = this.getSessionStorage();
            if (storage) {
                return storage.key(index);
            }
        } catch {
            // Fall back to memory storage if sessionStorage is blocked.
        }

        const keys = Object.keys(this.data);
        if (index >= keys.length) return null;
        return keys[index];
    }

    private getSessionStorage(): Storage|null {
        return isPlatformBrowser(this.platformId) ? window.sessionStorage : null;
    }
}

export function sessionStorageFactory(platformId: Object): SessionStorage {
    return new SessionStorage(platformId);
}

export const sessionStorageProvider: FactoryProvider = {
    provide: SESSION_STORAGE,
    useFactory: sessionStorageFactory,
    deps: [PLATFORM_ID]
};

export const SESSION_STORAGE_PROVIDERS = [
    sessionStorageProvider
];
